import React, { useMemo, useState } from 'react';
import { Building2, ChevronRight, Plus, Search, Users } from 'lucide-react';
import TeamAvatar from './ui/TeamAvatar';

/**
 * The organisations landing page — a directory, not a settings form.
 *
 * The first version was a form with tabs, which answered "how do I edit this
 * organisation" while the question people actually arrive with is "which of my
 * organisations, and what is in it". So the row leads with what identifies one
 * (initials tile, name, your role) and what sizes it (teams, seats, age), and
 * the teams themselves are chips — visible without a click, because the team
 * you want is usually the reason you came.
 */

function initials(name) {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] || '';
  const second = parts.length > 1 ? parts[parts.length - 1][0] : parts[0][1] || '';
  return (first + second).toUpperCase();
}

/**
 * A stable colour per organisation, derived from its id.
 *
 * Derived rather than stored: the tile is an identity aid, and an organisation
 * that changed colour when it was renamed would stop being one. Hashing the id
 * — which never changes — keeps it fixed for the organisation's whole life.
 */
function tileHue(id) {
  const s = String(id ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) % 360;
  return h;
}

const ROLE_FILTERS = [
  { key: 'owner', label: 'Owned by me' },
  { key: 'admin', label: 'Admin' },
  { key: 'member', label: 'Member' },
];

export default function OrganizationsList({
  organizations,
  teamsByOrg,
  onOpen,
  onManage,
  onCreate,
  onJoin,
}) {
  const [query, setQuery] = useState('');
  // No filter selected by default. Defaulting to "Owned by me" would hide the
  // organisations someone was added to — which for most people is all of them.
  const [role, setRole] = useState(null);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return organizations.filter((o) => {
      if (role && o.role !== role) return false;
      if (!q) return true;
      return (o.name || '').toLowerCase().includes(q) || (o.slug || '').toLowerCase().includes(q);
    });
  }, [organizations, query, role]);

  const totalTeams = useMemo(
    () => Object.values(teamsByOrg || {}).reduce((n, list) => n + (list?.length || 0), 0),
    [teamsByOrg]
  );

  return (
    <div className="orgx-scroll">
      <div className="orgx">
      <div className="orgx-crumbs">Organizations</div>

      <div className="orgx-head">
        <div>
          <h1 className="orgx-title">Organizations</h1>
          <p className="orgx-sub">
            {organizations.length} organization{organizations.length === 1 ? '' : 's'}
            {totalTeams > 0 && ` · ${totalTeams} team${totalTeams === 1 ? '' : 's'}`}
          </p>
        </div>
        <div className="orgx-head-actions">
          {onJoin && (
            <button type="button" className="orgx-btn" onClick={onJoin}>
              Join with code
            </button>
          )}
          <button type="button" className="orgx-btn orgx-btn--primary" onClick={onCreate}>
            <Plus size={15} aria-hidden /> Create organization
          </button>
        </div>
      </div>

      <div className="orgx-toolbar">
        <div className="orgx-search">
          <Search size={14} aria-hidden />
          <input
            value={query}
            placeholder="Search organizations"
            aria-label="Search organizations"
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="orgx-filters">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`orgx-filter${role === f.key ? ' orgx-filter--on' : ''}`}
              aria-pressed={role === f.key}
              // Clicking the active filter clears it. Without that, choosing
              // one is a one-way door and the only escape is a reload.
              onClick={() => setRole((r) => (r === f.key ? null : f.key))}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {visible.length === 0 && (
        <p className="orgx-none">
          {organizations.length === 0
            ? 'No organizations yet.'
            : 'No organization matches that.'}
        </p>
      )}

      <div className="orgx-cards">
        {visible.map((o) => {
          const teams = teamsByOrg?.[String(o.id)] || [];
          const canManage = o.role === 'owner' || o.role === 'admin';
          return (
            <div key={o.id} className="orgx-card">
              <div className="orgx-card-main">
                <span
                  className="orgx-tile"
                  style={{ '--tile-h': tileHue(o.id) }}
                  aria-hidden
                >
                  {initials(o.name)}
                </span>

                <div className="orgx-card-texts">
                  <div className="orgx-card-line">
                    <span className="orgx-card-name">{o.name}</span>
                    <span className={`orgx-role orgx-role--${o.role}`}>{o.role}</span>
                  </div>
                  <p className="orgx-card-meta">
                    {teams.length} team{teams.length === 1 ? '' : 's'}
                    {o.seats != null && ` · ${o.seats} seat${o.seats === 1 ? '' : 's'}`}
                    {o.inserted_at &&
                      ` · created ${new Date(o.inserted_at).toLocaleDateString('en-GB')}`}
                  </p>
                </div>

                <div className="orgx-card-actions">
                  {/* Manage is for people who can change something. Showing it
                      to a plain member would be an invitation to a 403. */}
                  {canManage && (
                    <button type="button" className="orgx-btn" onClick={() => onManage(o)}>
                      Manage
                    </button>
                  )}
                  <button
                    type="button"
                    className="orgx-btn orgx-btn--open"
                    onClick={() => onOpen(o)}
                  >
                    Open <ChevronRight size={14} aria-hidden />
                  </button>
                </div>
              </div>

              {teams.length > 0 && (
                <div className="orgx-chips">
                  {teams.map((t) => (
                    <span key={t.id} className="orgx-chip">
                      {/* The team's OWN avatar — its uploaded logo, or its
                          colour. An attached team already has an identity on
                          the Teams page; inventing a second one here would
                          make it look like a different team. */}
                      <TeamAvatar team={t} size={18} />
                      {t.name}
                    </span>
                  ))}
                </div>
              )}

              {teams.length === 0 && (
                <div className="orgx-chips">
                  <span className="orgx-chip orgx-chip--empty">
                    <Users size={12} aria-hidden /> No teams attached yet
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {organizations.length === 0 && (
        <div className="orgx-empty">
          <Building2 size={26} aria-hidden />
          <p className="orgx-empty-title">Nothing here yet</p>
          <p className="orgx-empty-hint">
            An organization sits above your teams: one place to administer them, and one
            invoice instead of several.
          </p>
        </div>
      )}
      </div>
    </div>
  );
}

export { initials, tileHue };
